/**
 * OO Расстановка (B.12-R8) — scoring engine. Ported from v3 utils/scoring.ts
 * (7-factor, lower = better) and extended with B.12-R8.9 per-slot gender as a
 * hard filter. Two layers:
 *   • scoreOperator/rankOperators — numeric engine (drives AUTO, never shown).
 *   • classify — maps the numeric result to a semantic marker with reasons in
 *     words (B.12.2 / R8.10): OO never sees score numbers, only ✓/⚠/× + cause.
 */

export interface BookingForAssign {
  id: string;
  questId: string;
  shortName: string;
  branchId: string;
  time: string; // "HH:MM"
  operatorsNeeded: number;
  duration: number; // minutes
  resetTime: number; // minutes
  /** Per-slot gender rule, e.g. "ММЖ" / "МN" (B.12-R8.9). Empty = any. */
  crewRule?: string;
}

export interface OperatorForAssign {
  id: string;
  name: string;
  fullstack: number;
  /** quests the operator can run (quest_id[]). */
  skills: string[];
  /** quest_id → rate (₽). */
  rates: Record<string, number>;
  /** availability hours "HH:MM"[]. Empty = always available. */
  avail: string[];
  /** "м" | "ж" | "" (B.12-R8.9). */
  gender?: string;
}

export type HallAdminMap = Record<string, string[]>; // branchId → operatorId[] (11.10: админов на филиал бывает двое)
export type AssignMap = Record<string, string[]>; // bookingId → operatorId[]

export interface ScoringConfig {
  noSkill: number;
  unavailable: number;
  overlap: number;
  crewFull: number;
  crewSpace: number;
  rateDiv: number;
  workloadMul: number;
  hallAdmin: number;
  noRate: number;
  threshold: number;
}

export const DEFAULT_CONFIG: ScoringConfig = {
  noSkill: 1000,
  unavailable: 200,
  overlap: 500,
  crewFull: 300,
  crewSpace: -10,
  rateDiv: 10,
  workloadMul: 50,
  hallAdmin: 100,
  noRate: 80,
  threshold: 500,
};

export interface ScoringFactor {
  name: string;
  penalty: number;
  reason: string;
}

export interface ScoringResult {
  operatorId: string;
  operatorName: string;
  score: number;
  factors: ScoringFactor[];
  suitable: boolean;
  rate: number;
  gameCount: number;
  /** Hard gender violation for the open slot (B.12-R8.9). */
  genderConflict: boolean;
}

function timeToMin(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}
function occupancyMin(b: BookingForAssign): number {
  return b.duration + b.resetTime + 15;
}
/** Overlap with a 90-min travel buffer between different branches. */
function bookingsOverlap(a: BookingForAssign, b: BookingForAssign): boolean {
  const s1 = timeToMin(a.time), e1 = s1 + occupancyMin(a);
  const s2 = timeToMin(b.time), e2 = s2 + occupancyMin(b);
  const travel = a.branchId !== b.branchId ? 90 : 0;
  return s1 - travel < e2 && s2 - travel < e1;
}

/**
 * Per-slot gender: does adding this operator keep the crew satisfiable against
 * crewRule? Counts М/Ж demand; N (any) absorbs the rest. Returns true when the
 * operator would violate the rule (no remaining slot of their gender).
 */
export function violatesGender(
  op: OperatorForAssign,
  rule: string | undefined,
  currentGenders: string[],
): boolean {
  if (!rule) return false;
  const g = (op.gender || "").toLowerCase();
  if (g !== "м" && g !== "ж") return false; // unknown gender never blocks
  let demandM = 0, demandF = 0, demandN = 0;
  for (const ch of rule.toUpperCase()) {
    if (ch === "М") demandM++; // Cyrillic Em
    else if (ch === "Ж") demandF++; // Cyrillic Zhe
    else if (ch === "N" || ch === "Н") demandN++; // any-slot
  }
  const haveM = currentGenders.filter((x) => x === "м").length;
  const haveF = currentGenders.filter((x) => x === "ж").length;
  // Blocked only when this gender already fills every slot it could take
  // (its gendered slots + the shared N slots). Conservative hard filter.
  if (g === "м") return haveM >= demandM + demandN;
  return haveF >= demandF + demandN;
}

export interface ScoreInput {
  operator: OperatorForAssign;
  booking: BookingForAssign;
  assignMap: AssignMap;
  allBookings: BookingForAssign[];
  hallAdmins: HallAdminMap;
  crewGenders?: string[];
  config?: ScoringConfig;
}

export function scoreOperator(input: ScoreInput): ScoringResult {
  const { operator, booking, assignMap, allBookings, hallAdmins } = input;
  const cfg = input.config ?? DEFAULT_CONFIG;
  const factors: ScoringFactor[] = [];
  let score = 0;

  const hasSkillData = operator.skills.length > 0;
  const knowsQuest = operator.skills.includes(booking.questId);
  if (hasSkillData && !knowsQuest) {
    score += cfg.noSkill;
    factors.push({ name: "skill", penalty: cfg.noSkill, reason: `не умеет ${booking.shortName}` });
  }

  const gameMin = timeToMin(booking.time);
  const isAvail = operator.avail.length === 0 || operator.avail.some((at) => Math.abs(timeToMin(at) - gameMin) <= 90);
  if (!isAvail) {
    score += cfg.unavailable;
    factors.push({ name: "availability", penalty: cfg.unavailable, reason: "вне графика" });
  }

  const myBookings: BookingForAssign[] = [];
  for (const [bid, ops] of Object.entries(assignMap)) {
    if (ops.includes(operator.id) && bid !== booking.id) {
      const other = allBookings.find((b) => b.id === bid);
      if (other) myBookings.push(other);
    }
  }
  const overlapping = myBookings.find((ob) => bookingsOverlap(ob, booking));
  if (overlapping) {
    score += cfg.overlap;
    factors.push({ name: "overlap", penalty: cfg.overlap, reason: `пересечение ${overlapping.time}` });
  }
  const gameCount = myBookings.length;

  const currentOps = assignMap[booking.id] ?? [];
  if (currentOps.length >= booking.operatorsNeeded) {
    score += cfg.crewFull;
    factors.push({ name: "crew", penalty: cfg.crewFull, reason: "состав полон" });
  } else {
    score += cfg.crewSpace;
  }

  const rate = operator.rates[booking.questId] ?? 0;
  if (rate > 0 && cfg.rateDiv > 0) {
    score += Math.round(rate / cfg.rateDiv);
  } else if (rate === 0 && cfg.noRate > 0) {
    score += cfg.noRate;
    factors.push({ name: "noRate", penalty: cfg.noRate, reason: "нет ставки" });
  }

  score += gameCount * cfg.workloadMul;
  if (gameCount >= 4) factors.push({ name: "workload", penalty: gameCount * cfg.workloadMul, reason: `нагрузка ${gameCount}` });

  if (Object.values(hallAdmins).some((ids) => ids.includes(operator.id))) {
    score += cfg.hallAdmin;
    factors.push({ name: "hallAdmin", penalty: cfg.hallAdmin, reason: "админ зала" });
  }

  const genderConflict = violatesGender(operator, booking.crewRule, input.crewGenders ?? []);
  if (genderConflict) {
    score += cfg.noSkill; // hard
    factors.push({ name: "gender", penalty: cfg.noSkill, reason: "пол" });
  }

  return {
    operatorId: operator.id,
    operatorName: operator.name,
    score,
    factors,
    suitable: score < cfg.threshold && !genderConflict,
    rate,
    gameCount,
    genderConflict,
  };
}

export function rankOperators(
  operators: OperatorForAssign[],
  booking: BookingForAssign,
  assignMap: AssignMap,
  allBookings: BookingForAssign[],
  hallAdmins: HallAdminMap,
  crewGenders?: string[],
  config?: ScoringConfig,
): ScoringResult[] {
  return operators
    .map((op) => scoreOperator({ operator: op, booking, assignMap, allBookings, hallAdmins, crewGenders, config }))
    .sort((a, b) => a.score - b.score || a.operatorName.localeCompare(b.operatorName, "ru"));
}

/* ── Semantic layer — words, never numbers (B.12.2 / R8.10) ──────────────── */

export type FitStatus = "ok" | "warn" | "bad";
const HARD = new Set(["skill", "availability", "overlap", "gender"]);

export interface FitMarker {
  status: FitStatus;
  reasons: string[];
}

/** Map a numeric result to the dropdown marker shown to OO. */
export function classify(r: ScoringResult): FitMarker {
  const hard = r.factors.filter((f) => HARD.has(f.name));
  if (hard.length > 0) return { status: "bad", reasons: hard.map((f) => f.reason) };
  const soft = r.factors.filter((f) => ["noRate", "hallAdmin", "workload"].includes(f.name));
  if (soft.length > 0) return { status: "warn", reasons: soft.map((f) => f.reason) };
  return { status: "ok", reasons: [] };
}

/**
 * Chip warn-state (B.12.2): an assigned operator's name turns red when they
 * have no rate on this quest, are off-schedule, or violate the gender rule.
 */
export function chipIsWarn(op: OperatorForAssign, booking: BookingForAssign, crewGenders: string[]): boolean {
  return chipReasons(op, booking, crewGenders).length > 0;
}

/** Human conflict reasons for an assigned operator (B.12-R8.10 long-press). */
export function chipReasons(op: OperatorForAssign, booking: BookingForAssign, crewGenders: string[]): string[] {
  const out: string[] = [];
  if (op.skills.length > 0 && !op.skills.includes(booking.questId)) out.push(`не умеет ${booking.shortName}`);
  else if (op.rates[booking.questId] == null && op.skills.length === 0) out.push("нет ставки");
  if (op.avail.length > 0 && !op.avail.some((at) => Math.abs(timeToMin(at) - timeToMin(booking.time)) <= 90)) out.push("вне графика");
  if (violatesGender(op, booking.crewRule, crewGenders)) out.push("пол");
  return out;
}
